import { htxWebSocket } from './wsClient';
import HTXClient from '../../apis/htx/HTXClient';

const subscriptions = new Set();
const htxClient = new HTXClient();



export const updateMarkets = (currentMarkets, marketData) => {
  const updatedMarkets = [...currentMarkets]
  
  // Handle single market update
  if (!Array.isArray(marketData)) {
    const existingIndex = updatedMarkets.findIndex(x => x.symbol === marketData.symbol)
    if (existingIndex === -1) {
      updatedMarkets.push(marketData)
    } else {
      updatedMarkets[existingIndex] = {
        ...updatedMarkets[existingIndex],
        ...marketData
      }
    }
    return updatedMarkets
  }
  
  // Handle multiple markets update (from overviewv2)
  marketData.forEach(market => {
    const existingIndex = updatedMarkets.findIndex(x => x.symbol === market.symbol)
    if (existingIndex === -1) {
      updatedMarkets.push(market)
    } else {
      updatedMarkets[existingIndex] = {
        ...updatedMarkets[existingIndex],
        ...market
      }
    }
  })
  
  return updatedMarkets
}

export const updateKlines = (currentKlines, newKline) => {
  if (currentKlines.symbol?.replace("-", "")?.replace("/", "")?.toLowerCase() !== newKline?.symbol?.replace("-", "")?.replace("/", "")?.toLowerCase()) return currentKlines
  return {
    ...currentKlines,
    data: [...currentKlines.data, newKline]
  }
}

export const updateAllKlines = (currentAllKlines, newKline) => {
  const symbol = newKline.symbol
  const currentSymbolKlines = currentAllKlines[symbol] || []
  
  let updatedKlines = [...currentSymbolKlines]
  updatedKlines.reverse()
  
  if (updatedKlines.length >= 20) {
    updatedKlines = updatedKlines?.slice(0, 19)
  }
  updatedKlines.push(newKline)
  updatedKlines.reverse()
  
  return {
    ...currentAllKlines,
    [symbol]: updatedKlines
  }
}

export const updateOrderBooks = (currentOrderBooks, newData) => {
  const updatedBooks = [...currentOrderBooks]
  const existingIndex = updatedBooks.findIndex(x => x.pairName === newData.pairName)
  if (existingIndex === -1) {
    updatedBooks.push(newData)
  } else {
    updatedBooks[existingIndex] = {
      ...updatedBooks[existingIndex],
      ...newData
    }
  }
  
  return updatedBooks
}

export const updateTrades = (currentTrades, newData) => {
  const symbol = newData.symbol
  const currentSymbolTrades = currentTrades[symbol] || []
  
  let updatedTrades = [...currentSymbolTrades]
  updatedTrades.reverse()
  
  updatedTrades.push(newData)
  if (updatedTrades.length >= 20) {
    updatedTrades = updatedTrades?.slice(1, 20)
  }
  updatedTrades.reverse()
  
  return {
    ...currentTrades,
    [symbol]: updatedTrades
  }
}

export const updateAllTrades = (currentTrades, newData = []) => {
  const updatedTrades = { ...currentTrades }
  newData?.forEach(trade => {
    const symbol = trade?.symbol?.replace('/', '')?.replace('-', '')?.toLowerCase();
    const currentSymbolTrades = updatedTrades?.[symbol] || []
    
    if (currentSymbolTrades.length < 20) {
      currentSymbolTrades?.push(trade)
      currentSymbolTrades?.sort((a, b) => b?.timestamp - a?.timestamp)
      updatedTrades[symbol] = currentSymbolTrades
    }
  })
  
  return updatedTrades
}

export function initializeSubscriptions(markets) {
  if (!htxWebSocket.isConnected) {
    console.warn('WebSocket not connected. Will subscribe when connected.');
    // Queue overviewv2 subscription for frequent market data updates
    htxWebSocket.subscriptions.set('market.overviewv2', {channel:'market.overviewv2', isConnected:true});
    console.log('Queued market.overviewv2 subscription for frequent data updates');
    return;
  }

  // If already connected, subscribe to overviewv2 immediately
  console.log('WebSocket connected. Subscribing to market.overviewv2 for frequent data updates');
  subscribeToOverviewV2();
}

// Subscribe to market overview v2 for frequent data updates
export function subscribeToOverviewV2() {
  const channel = 'market.overviewv2';
  
  // Check if already subscribed to avoid duplicate subscriptions
  if (subscriptions.has(channel)) {
    console.log('Already subscribed to market.overviewv2');
    return;
  }
  
  if (!htxWebSocket.isConnected) {
    // Queue the subscription for when connected
    htxWebSocket.subscriptions.set(channel, {channel, isConnected:true});
    console.log('Queued overviewv2 subscription');
    return;
  }
  
  // Subscribe immediately if connected
  console.log('Subscribing to market.overviewv2 for frequent market data updates');
  
  // Add to subscriptions set to track active subscriptions
  subscriptions.add(channel);
  
  // Use the WebSocket client's subscribe method
  return htxWebSocket.subscribe(channel);
}

// Fetch initial depth data from REST API and update state
export async function fetchInitialDepthData(symbol, setState) {
  if (!symbol || !setState) return;
  
  try {
    console.log(`Fetching initial depth data for market: ${symbol}`);
    const response = await htxClient.fetchMarketDepth(symbol);
    
    if (response.success && response.data) {
      const orderBookData = {
        pairName: response.data.pairName,
        bids: response.data.bids || [],
        asks: response.data.asks || [],
        timestamp: response.data.timestamp || Date.now()
      };
      
      // Update state with initial depth data
      setState(prevState => ({
        ...prevState,
        orderBooks: updateOrderBooks(prevState.orderBooks || [], orderBookData)
      }));
      
      console.log(`Initial depth data loaded for market: ${symbol}`);
    } else {
      console.warn(`Failed to fetch initial depth data for ${symbol}:`, response.error);
    }
  } catch (error) {
    console.error(`Error fetching initial depth data for ${symbol}:`, error);
  }
}

// Subscribe to depth events for a specific market with initial data fetch
export async function subscribeToMarketDepth(symbol, setState = null) {
  if (!symbol) return;
  
  const channel = `market.${symbol}.depth.step0`;
  
  // Check if already subscribed to avoid duplicate subscriptions
  if (subscriptions.has(channel)) {
    console.log(`Already subscribed to depth for market: ${symbol}`);
    return;
  }
  
  // Fetch initial depth data first if setState is provided
  if (setState) {
    await fetchInitialDepthData(symbol, setState);
  } 
  
  if (!htxWebSocket.isConnected) {
    // Queue the subscription for when connected
    htxWebSocket.subscriptions.set(channel, {channel, isConnected:true});
    console.log(`Queued depth subscription for market: ${symbol}`);
    return;
  }
  
  // Subscribe immediately if connected
  console.log(`Subscribing to depth for market: ${symbol}`);
  subscribeToDataFeed(symbol, 'depth.step0');
}

// Subscribe to trade events for a specific market
export function subscribeToMarketTrades(symbol) {
  if (!symbol) return;
  
  const channel = `market.${symbol}.trade.detail`;
  
  // Check if already subscribed to avoid duplicate subscriptions
  if (subscriptions.has(channel)) {
    console.log(`Already subscribed to trades for market: ${symbol}`);
    return;
  }
  
  if (!htxWebSocket.isConnected) {
    // Queue the subscription for when connected
    htxWebSocket.subscriptions.set(channel, {channel, isConnected:true});
    console.log(`Queued trade subscription for market: ${symbol}`);
    return;
  }
  
  // Subscribe immediately if connected
  console.log(`Subscribing to trades for market: ${symbol}`);
  subscribeToDataFeed(symbol, 'trade.detail');
}

// Unsubscribe from depth events for a specific market
export function unsubscribeFromMarketDepth(symbol) {
  if (!symbol) return;
  
  const channel = `market.${symbol}.depth.step0`;
  
  // Remove from subscriptions map
  htxWebSocket.subscriptions.delete(channel);
  
  // Send unsubscribe message if connected
  if (htxWebSocket.isConnected) {
    const unsubscription = {
      unsub: channel,
      id: `${channel}_unsub_${Date.now()}`
    };
    htxWebSocket.send(unsubscription);
  }
  
  // Remove from local subscriptions set
  subscriptions.delete(channel);
}

// Unsubscribe from trade events for a specific market
export function unsubscribeFromMarketTrades(symbol) {
  if (!symbol) return;
  
  const channel = `market.${symbol}.trade.detail`;
  
  // Remove from subscriptions map
  htxWebSocket.subscriptions.delete(channel);
  
  // Send unsubscribe message if connected
  if (htxWebSocket.isConnected) {
    const unsubscription = {
      unsub: channel,
      id: `${channel}_unsub_${Date.now()}`
    };
    htxWebSocket.send(unsubscription);
  }
  
  // Remove from local subscriptions set
  subscriptions.delete(channel);
}

// Subscribe to detail events for a specific market
export function subscribeToMarketDetail(symbol) {
  if (!symbol) return;
  
  const channel = `market.${symbol}.detail`;
  
  // Check if already subscribed to avoid duplicate subscriptions
  if (subscriptions.has(channel)) {
    console.log(`Already subscribed to detail for market: ${symbol}`);
    return;
  }
  
  if (!htxWebSocket.isConnected) {
    // Queue the subscription for when connected
    htxWebSocket.subscriptions.set(channel, {channel, isConnected:true});
    console.log(`Queued detail subscription for market: ${symbol}`);
    return;
  }
  
  // Subscribe immediately if connected
  console.log(`Subscribing to detail for market: ${symbol}`);
  subscribeToDataFeed(symbol, 'detail');
}

// Unsubscribe from detail events for a specific market
export function unsubscribeFromMarketDetail(symbol) {
  if (!symbol) return;
  
  const channel = `market.${symbol}.detail`;
  
  // Remove from subscriptions map
  htxWebSocket.subscriptions.delete(channel);
  
  // Send unsubscribe message if connected
  if (htxWebSocket.isConnected) {
    const unsubscription = {
      unsub: channel,
      id: `${channel}_unsub_${Date.now()}`
    };
    htxWebSocket.send(unsubscription);
  }
  
  // Remove from local subscriptions set
  subscriptions.delete(channel);
}

async function subscribeToDataFeed(symbol, dataType) {
  const channel = `market.${symbol}.${dataType}`;
  
  // Add to subscriptions set to track active subscriptions
  subscriptions.add(channel);
  
  // Use the WebSocket client's subscribe method
  return htxWebSocket.subscribe(channel);
}

